import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { sharkLeft } from "@/constants";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/tabs/Tab4";
import { useEffect, useState } from "react";
import { Tab } from "@/types";
import { useGetRecentGames } from "@/services/game.service";

const tabs: Tab[] = [
  {
    text: "All",
    value: "",
  },
  {
    text: "Instant Games",
    value: "instant",
  },
  {
    text: "Slots",
    value: "slots",
  },
  {
    text: "Live Casino",
    value: "live_casino",
  },
  {
    text: "Table Games",
    value: "side_bets",
  },
  {
    text: "Crash Games",
    value: "battles",
  },
];

const headers = ["Game", "Player", "Time", "Wager", "Payout"];
const rowsPerPageOptions = [3, 5, 10];

interface BetsProps {
  hideTabs?: boolean;
}

const Bets = ({ hideTabs }: BetsProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [focusedTab, setFocusedTab] = useState<number>(0);
  const {
    data: games,
    refetch,
    isSuccess,
  } = useGetRecentGames(currentPage, rowsPerPage, tabs[focusedTab].value);

  useEffect(() => {
    refetch();
  }, [games, isSuccess, focusedTab]);

  const handleChangeRowsPerPage = (e: any) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleClickNextPage = () => {
    if (currentPage >= games?.pagination?.totalPages) {
      return;
    }
    setCurrentPage((prevPage) => prevPage + 1);
    refetch();
  };
  const handleClickPrevPage = () => {
    if (currentPage == 1) {
      return;
    }
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
    refetch();
  };

  const totalRows = games?.pagination?.totalResults || 0;
  const startRow = (currentPage - 1) * rowsPerPage + 1;
  const endRow = startRow + games?.data?.length - 1;
  const rowsRange = !isSuccess
    ? "Loading..."
    : `${startRow}-${endRow} out of ${totalRows}`;

  return (
    <div className="w-full">
      <div className="flex flex-row items-center justify-between">
        <p className="text-lg font-[500] text-white">Recent Bets</p>
        <div className="ml-[15px] justify-start rounded-lg border-2 border-white px-2 py-1 text-white lg:hidden">
          <label htmlFor="rowsPerPage" className="font-sans text-white sm:mr-2">
            Rows per page:
          </label>
          <select
            id="rowsPerPage"
            className="select-menu font-open-sans rounded border-none bg-transparent px-2 py-1 font-normal"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
          >
            {rowsPerPageOptions.map((option) => (
              <option
                key={option}
                value={option}
                className="font-open-sans text-gray-600 sm:font-normal"
              >
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Tabs defaultValue={tabs[0].value}>
        <TabsList className={`flex w-full justify-between gap-4 text-white`}>
          <div
            className={`flex w-full justify-between lg:w-1/2 lg:justify-start ${hideTabs && "hidden"}`}
          >
            {tabs.map((tab, index) => {
              const isSelected = focusedTab === index;

              return (
                <TabsTrigger
                  key={index}
                  value={tab.value}
                  className="flex h-[60px] w-[45px] flex-col justify-between gap-2 bg-transparent text-[10px] font-[500] text-white data-[state=active]:bg-transparent lg:w-auto"
                  onClick={() => setFocusedTab(index)}
                >
                  <p
                    className={`flex h-full items-center justify-center text-wrap text-sm leading-none ${isSelected && "text-[#79BFFF]"}`}
                  >
                    {tab.text}
                  </p>
                  <div
                    className={`h-[5px] w-[45px] bg-[#79BFFF] lg:w-full ${isSelected ? "opacity-100" : "opacity-50"}`}
                  ></div>
                </TabsTrigger>
              );
            })}
          </div>
          <div
            className={`ml-[15px] hidden rounded-lg border-2 border-white px-2 py-1 text-white lg:flex ${hideTabs && "lg:hidden"}`}
          >
            <label
              htmlFor="rowsPerPage"
              className="font-sans text-white sm:mr-2"
            >
              Rows per page:
            </label>
            <select
              id="rowsPerPage"
              className="select-menu font-open-sans rounded border-none bg-transparent px-2 py-1 font-normal"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
            >
              {rowsPerPageOptions.map((option) => (
                <option
                  key={option}
                  value={option}
                  className="font-open-sans text-gray-600 sm:font-normal"
                >
                  {option}
                </option>
              ))}
            </select>
          </div>
        </TabsList>
        {tabs.map((tab, index) => (
          <TabsContent key={index} value={tab.value}>
            <div className="relative flex items-center justify-center">
              <Table className=" relative border-separate border-spacing-x-0 border-spacing-y-1 ">
                <TableHeader className="bg-gradient-to-t from-[#003BD2]/50 to-[#1181E8]/30">
                  <TableRow className="text-base text-white">
                    {headers.map((header, index) => {
                      return (
                        <TableHead
                          key={index}
                          className="p-2 text-start text-white"
                        >
                          {header}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                {isSuccess && (
                  <TableBody className="space-y-2 rounded-2xl rounded-[20px] bg-dark-blue">
                    {games?.data?.map((bet: any, index: number) => (
                      <TableRow
                        key={index}
                        className={`rounded-lg bg-primary-background text-white`}
                      >
                        <TableCell className="p-2.5 pr-8">
                          <div className="flex items-center">
                            <img
                              src={
                                bet?.gameThumbnail
                                  ? bet.gameThumbnail
                                  : (sharkLeft as string)
                              }
                              alt="icon"
                              className="mr-2 h-6 w-6 rounded-lg"
                            />
                            <span className="text-nowrap">{bet?.gameName}</span>
                          </div>
                        </TableCell>
                        <TableCell className={`p-2.5 pr-8`}>
                          <div className="flex items-center gap-2">
                            <img
                              src={bet?.userImage}
                              alt="proicon"
                              className="h-6 w-6 rounded-full border border-white"
                            />
                            <span className="text-nowrap">{bet?.userName}</span>
                          </div>
                        </TableCell>
                        <TableCell className={`text-nowrap p-2.5`}>
                          {" "}
                          {new Date(bet?.createdAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell className={`p-2.5`}>${bet?.bet}</TableCell>
                        <TableCell className=" p-2.5">
                          ${bet?.winning.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
            </div>

            {/* Table Pagination */}
            <div className="flex h-full	w-full items-center justify-between rounded-2xl bg-primary-background p-2 text-sm">
              <button onClick={handleClickPrevPage} className="text-xs">
                <PaginationArrowRight className="rotate-180" />
              </button>
              <div className="text-white">
                <p>{rowsRange}</p>
              </div>
              <button onClick={handleClickNextPage} className="text-xs">
                <PaginationArrowRight />
              </button>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

const PaginationArrowRight = ({ className }: any) => {
  return (
    <svg
      className={className}
      width="38"
      height="38"
      viewBox="0 0 38 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g filter="url(#filter0_d_231_8996)">
        <circle
          cx="13"
          cy="13"
          r="13"
          transform="matrix(4.37114e-08 1 1 -4.37114e-08 6 0)"
          fill="#69EAFF"
        />
        <path
          d="M23.0958 13.0396C23.0958 12.8951 23.0687 12.755 23.0146 12.6192C22.9604 12.4835 22.8882 12.3708 22.7979 12.2812L17.8146 7.29792C17.616 7.09931 17.3632 7 17.0562 7C16.7493 7 16.4965 7.09931 16.2979 7.29792C16.0993 7.49653 16 7.74931 16 8.05625C16 8.36319 16.0993 8.61597 16.2979 8.81458L20.5229 13.0396L16.2979 17.2646C16.0993 17.4632 16 17.716 16 18.0229C16 18.3299 16.0993 18.5826 16.2979 18.7812C16.4965 18.9799 16.7493 19.0792 17.0562 19.0792C17.3632 19.0792 17.616 18.9799 17.8146 18.7812L22.7979 13.7979C22.9062 13.6896 22.9832 13.5722 23.0287 13.4458C23.0742 13.3194 23.0966 13.184 23.0958 13.0396Z"
          fill="#00113A"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_231_8996"
          x="0"
          y="0"
          width="38"
          height="38"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="6" />
          <feGaussianBlur stdDeviation="3" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0.117647 0 0 0 0 0.411765 0 0 0 0.5 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_231_8996"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_231_8996"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
};

export default Bets;
